import React, { Fragment, useState } from 'react';
import {
  Button,
  useRefresh,
  useNotify,
  useUnselectAll,
  useDeleteMany,
  CRUD_DELETE_MANY
} from 'react-admin';
import { makeStyles, alpha } from '@material-ui/core';
import ResourceUploadApi from '../../core/apis/ResourceUploadApi';
import Confirm from '../../core/components/forked/layout/Confirm';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const useStyles = makeStyles(theme => ({
  button: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginRight: '9px'
  },
  deleteButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      // Reset on mouse devices
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    },
    marginRight: '9px',
    '& svg': { display: 'none' }
  }
}));

const ResourceUploadListBulkActions = props => {
  const {
    handleManageDocumentPropertiesOpen,
    hasDeletePermission,
    hasEditPermission,
    hasApprovePermission,
    selectedIds,
    setShowLoader,
    resource,
    showLoader
  } = props;
  const classes = useStyles();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const unselectAll = useUnselectAll();
  const [openConfirmSuspend, setOpenConfirmSuspend] = useState(false);
  const [openConfirmApprove, setOpenConfirmApprove] = useState(false);
  const [openConfirmDelete, setopenConfirmDelete] = useState(false);
  const [openConfirmPending, setOpenConfirmPending] = useState(false);

  const handleApprove = async () => {
    setOpenConfirmApprove(false);
    if (showLoader) return;
    setShowLoader(true);
    try {
      const data = {
        batchIds: selectedIds
      };
      await ResourceUploadApi.setMultipleActive(data);
      refresh();
      unselectAll(resource);
    } catch (e) {
      notifyServerError(e);
    } finally {
      setShowLoader(false);
    }
  };

  const handlePending = async () => {
    setOpenConfirmPending(false);
    if (showLoader) return;
    setShowLoader(true);
    try {
      const data = {
        batchIds: selectedIds
      };
      await ResourceUploadApi.setMultiplePending(data);
      refresh();
      unselectAll(resource);
    } catch (e) {
      notifyServerError(e);
    } finally {
      setShowLoader(false);
    }
  };
  const handleSuspend = async () => {
    setOpenConfirmSuspend(false);
    if (showLoader) return;
    setShowLoader(true);
    try {
      const data = {
        batchIds: selectedIds
      };
      await ResourceUploadApi.setMultipleSuspended(data);
      refresh();
      unselectAll(resource);
    } catch (e) {
      notifyServerError(e);
    } finally {
      setShowLoader(false);
    }
  };
  const handleEditClick = async () => {
    await handleManageDocumentPropertiesOpen(selectedIds[0]);
    refresh();
    unselectAll(resource);
  };

  const [deleteMany] = useDeleteMany(resource, selectedIds, {
    action: CRUD_DELETE_MANY,
    onSuccess: () => {
      refresh();
      notify('ra.notification.deleted', {
        messageArgs: { smart_count: selectedIds.length }
      });
      unselectAll(resource);
      setShowLoader(false);
    },
    onFailure: error => {
      notifyServerError(error, 'ra.notification.http_error');
      setShowLoader(false);
    }
  });

  const handleDeleteClick = async () => {
    //Close the confirm dialog
    setopenConfirmDelete(false);
    //Show loader
    setShowLoader(true);
    deleteMany(selectedIds);
  };

  const editVisible = selectedIds && selectedIds.length === 1;
  return (
    <Fragment>
      {editVisible && hasEditPermission && (
        <Button
          className={classes.button}
          variant="outlined"
          color="primary"
          label="Edit"
          onClick={handleEditClick}
        />
      )}
      {hasApprovePermission && (
        <>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            label="Approve"
            onClick={() => setOpenConfirmApprove(true)}
          />
          <Confirm
            isOpen={openConfirmApprove}
            title="Approve Batches"
            content="All documents contained within the selected Fund-Batch combinations will be approved."
            onConfirm={handleApprove}
            onClose={() => setOpenConfirmApprove(false)}
          />
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            label="Pending"
            onClick={() => setOpenConfirmPending(true)}
          />
          <Confirm
            isOpen={openConfirmPending}
            title="Set Batches As Pending"
            content="All documents contained within the selected Fund-Batch combinations will be set as pending."
            onConfirm={handlePending}
            onClose={() => setOpenConfirmPending(false)}
          />
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            label="Suspend"
            onClick={() => setOpenConfirmSuspend(true)}
            //onClick={handleSuspend}
          />
          <Confirm
            isOpen={openConfirmSuspend}
            title="Suspend Batches"
            content="All documents contained within the selected Fund-Batch combinations will be suspended."
            onConfirm={handleSuspend}
            onClose={() => setOpenConfirmSuspend(false)}
          />
        </>
      )}
      {hasDeletePermission && (
        <>
          <Button
            className={classes.deleteButton}
            variant="outlined"
            color="primary"
            label="Delete"
            onClick={() => setopenConfirmDelete(true)}
          />
          <Confirm
            isOpen={openConfirmDelete}
            title="Delete Batches"
            content="All documents belonging to the selected Fund-Batch combinations will be permanently deleted."
            onConfirm={handleDeleteClick}
            onClose={() => setopenConfirmDelete(false)}
          />
        </>
      )}
    </Fragment>
  );
};

export default ResourceUploadListBulkActions;
